/**
 * Guidelines to be displayed underneath a scratchpad
 */
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingMedium,
    HeadingSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslateYet} from "@repo/i18n";

export default function Guidelines(): React.ReactElement {
    return (
        <View style={styles.wrapper}>
            <LabelMedium tag="p" style={styles.paragraph}>
                {doNotTranslateYet`To keep the community focused on learning, we require all programs and discussion posts to align with Khan Academy’s mission. Please review the guidelines below as you create your programming masterpieces. Thank you for making our community safe, supportive, and appropriate for all learners!`}
            </LabelMedium>
            <HeadingMedium tag="h2" style={styles.newSectionStart}>
                {doNotTranslateYet`Program guidelines`}
            </HeadingMedium>
            <LabelMedium tag="p" style={styles.paragraph}>
                {doNotTranslateYet`Programs that don’t meet the following guidelines may be hidden from the hotlist or from the public gallery view. Inappropriate discussion posts may also be deleted. Learners who persistently break these rules may be banned from community activity.`}
            </LabelMedium>
            <LabelMedium tag="p" style={styles.paragraph}>
                {doNotTranslateYet`A program or post is considered inappropriate if it includes any of the following:`}
            </LabelMedium>
            <HeadingSmall tag="h3" style={styles.newSectionStart}>
                {doNotTranslateYet`Offensive content`}
            </HeadingSmall>
            <ul className={css(styles.list)}>
                <LabelMedium tag="li" style={styles.listItem}>
                    {doNotTranslateYet`Discriminatory or offensive language.`}
                </LabelMedium>
                <LabelMedium tag="li" style={styles.listItem}>
                    {doNotTranslateYet`Sexual or violent content, or any other content that could reasonably be deemed inappropriate for an educational setting.`}
                </LabelMedium>
            </ul>
            <HeadingSmall tag="h3" style={styles.newSectionStart}>
                {doNotTranslateYet`Off-topic content`}
            </HeadingSmall>
            <ul className={css(styles.list)}>
                <LabelMedium tag="li" style={styles.listItem}>
                    {doNotTranslateYet`Advertisements or non-helpful links to third party sites.`}
                </LabelMedium>
                <LabelMedium tag="li" style={styles.listItem}>
                    {doNotTranslateYet`Controversial or divisive topics including religion, politics, or personal information that may reveal details that compromise learner privacy.`}
                </LabelMedium>
                <LabelMedium tag="li" style={styles.listItem}>
                    {doNotTranslateYet`Social groups, or programs that generate social chats or other posts not related to programming in the discussion thread.`}
                </LabelMedium>
                <LabelMedium tag="li" style={styles.listItem}>
                    {doNotTranslateYet`Asking others to vote up a program.`}
                </LabelMedium>
                <LabelMedium tag="li" style={styles.listItem}>
                    {doNotTranslateYet`Garnering or receiving votes from a voting ring, defined as a group of people who conspire to vote up each others’ programs.`}
                </LabelMedium>
            </ul>
            <HeadingSmall tag="h3" style={styles.newSectionStart}>
                {doNotTranslateYet`Plagiarism`}
            </HeadingSmall>
            <LabelMedium tag="p" style={styles.paragraph}>
                {doNotTranslateYet`If you are inspired by someone else’s program, you can create a spin-off of that program by pressing the Spin-off button. Creating a spin-off properly gives credit to the original creator by linking to the original program.`}
            </LabelMedium>
            <LabelMedium tag="p" style={styles.paragraph}>
                {doNotTranslateYet`A program is considered plagiarism if it copies and pastes someone else's work into a new program instead of creating a spin-off. If a program copies and pastes someone else’s work from several programs, it must give credit to the creators in the comments at the top of the program. Otherwise, it is considered plagiarism.`}
            </LabelMedium>
        </View>
    );
}

const styles = StyleSheet.create({
    wrapper: {
        gap: spacing.small_12,
        alignItems: "flex-start",
        flexShrink: 0,
        paddingBottom: spacing.xxLarge_48,
    },
    newSectionStart: {
        marginTop: spacing.small_12,
    },
    list: {
        listStyle: "inherit",
        paddingLeft: 20,
    },
    listItem: {
        display: "list-item",
    },
    paragraph: {
        margin: 0,
    },
});
